import React from "react";
import { ResponsiveTable } from "../ResponsiveTable";
import {
  Box,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { Link } from "react-router-dom";
import { MULISH } from "../../WireGuardTunnels/VpnDebugger/vpnDebugConstants";
import { VpnDebuggerPanel } from "../../WireGuardTunnels/VpnDebugger/VpnDebuggerPanel";
import { useVpnDebuggerStore } from "../../WireGuardTunnels/VpnDebugger/vpnDebuggerStore/vpnDebuggerStore";
import { useTroubleshootingStore } from "../troubleshootingStore/troubleshootingStore";
import {
  Card,
  CardHeader,
  CELL_SX,
  GapNotice,
  SHADED_HEAD_CELL_SX,
  Hint,
  Mono,
  Pill,
  SampleChip,
  StatusDot,
  TS_COLORS,
  TS_SUBSECTION_BG,
  TsButton,
} from "../TroubleshootingUi";
import {
  formatBytes,
  formatRelative,
} from "../TroubleshootingUtils/formatters";
import {
  siteCameraHosts,
  useScopedTunnels,
  useServerName,
} from "../TroubleshootingUtils/useScope";
import { noSiteTunnelMessage } from "../TroubleshootingUtils/siteTunnel";
import {
  getPeerHealth,
  peerHealthSummary,
  PEER_HEALTH_LABEL,
} from "../TroubleshootingUtils/wireGuardHealth";

const SubTab = ({
  on,
  onClick,
  children,
}: {
  on: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) => (
  <Box
    component="button"
    type="button"
    role="tab"
    aria-selected={on}
    onClick={onClick}
    sx={{
      ...MULISH,
      background: "none",
      border: 0,
      borderBottom: `3px solid ${on ? TS_COLORS.wg : "transparent"}`,
      px: 1.75,
      pt: 1.5,
      pb: 1.25,
      fontWeight: 700,
      fontSize: 14,
      letterSpacing: "0.02em",
      textTransform: "uppercase",
      color: on ? TS_COLORS.wg : TS_COLORS.ink2,
      cursor: "pointer",
    }}
  >
    {children}
  </Box>
);

const PeerHealthTable = ({ onCopy }: { onCopy: () => void }) => {
  const {
    telemetry,
    setPendingDebugTunnelId,
    setWgSubTab,
    tunnelsLoading,
    tunnelsError,
    scope,
    siteDetail,
    cameraLiveness,
    siteCameras,
  } = useTroubleshootingStore();
  const tunnels = useScopedTunnels();
  const getServerName = useServerName();
  const tele = telemetry?.status === "ok" ? telemetry.data : null;
  /**
   * What an empty telemetry cell means. The endpoint exists (ENG-1668), so a
   * blank is the gateway not having reported yet: `actuate-wireguard-gateway`
   * writes these on its ~10 min run, and a gateway still on an older release
   * writes nothing at all. Only say "needs API" when the call itself failed.
   */
  const missingTelemetry = tele ? "not reported yet" : "needs API";
  const peerSummary = peerHealthSummary(tunnels);

  return (
    <Card
      sx={{
        mt: 0,
        border: 0,
        borderTop: 0,
        borderRadius: 0,
        boxShadow: "none",
      }}
    >
      <CardHeader
        title="Peer health"
        hint={
          <>
            Read-only.{" "}
            {tunnels.length === 0 ? null : peerSummary.problem ? (
              <b style={{ color: TS_COLORS.red }}>{peerSummary.text}</b>
            ) : (
              peerSummary.text
            )}{" "}
            Adding, editing, bulk upload and export live on the{" "}
            <Link
              to="/wireguardtunnels"
              style={{ color: TS_COLORS.blue, fontWeight: 700 }}
            >
              WireGuard page
            </Link>
            .
          </>
        }
        right={
          <>
            {telemetry?.status === "ok" && telemetry.sample && <SampleChip />}
            <TsButton variant="quiet" size="sm" onClick={onCopy}>
              Copy report
            </TsButton>
          </>
        }
      />
      {tunnelsError && (
        <Box sx={{ p: 2.5 }}>
          <Typography sx={{ ...MULISH, color: TS_COLORS.red }}>
            {tunnelsError}
          </Typography>
        </Box>
      )}
      {!tunnelsError && tunnels.length === 0 && (
        <Box sx={{ p: 3 }}>
          <Hint>
            {tunnelsLoading
              ? "Loading tunnels…"
              : scope === "site"
              ? noSiteTunnelMessage(
                  siteDetail,
                  siteCameraHosts(cameraLiveness, siteCameras)
                )
              : "No WireGuard tunnels are configured for your account."}
          </Hint>
        </Box>
      )}
      {tunnels.length > 0 && (
        <Box sx={{ overflowX: "auto" }}>
          <ResponsiveTable minWidth={1100}>
            <TableHead>
              <TableRow>
                {[
                  "Tunnel name",
                  "WireGuard server",
                  "Your endpoint",
                  "Subnet (allowed IPs)",
                  "Last handshake",
                  "Transfer, 10 min",
                  "Status",
                  "",
                ].map((h) => (
                  <TableCell key={h} sx={SHADED_HEAD_CELL_SX}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {tunnels.map((t) => {
                const health = getPeerHealth(t);
                const row = tele ? tele[t.id] : undefined;
                const isStale = health === "stale";
                const rx = row?.rx_bytes_10m ?? null;
                return (
                  <TableRow
                    key={t.id}
                    sx={isStale ? { backgroundColor: "#fffaf5" } : undefined}
                    data-testid={`peer-row-${t.id}`}
                  >
                    <TableCell sx={CELL_SX}>
                      <b>{t.name}</b>
                      <Box>
                        <Hint>
                          key{" "}
                          <Mono>
                            {t.client_public_key.slice(0, 6)}…
                            {t.client_public_key.slice(-4)}
                          </Mono>
                        </Hint>
                      </Box>
                    </TableCell>
                    <TableCell sx={CELL_SX}>
                      {getServerName(t.server)}
                      <Box>
                        <Hint>tunnel IP {t.client_tunnel_ip}</Hint>
                      </Box>
                    </TableCell>
                    <TableCell sx={CELL_SX}>
                      {row?.endpoint ? (
                        <Mono>{row.endpoint}</Mono>
                      ) : (
                        <Hint>{missingTelemetry}</Hint>
                      )}
                      <Box>
                        <Hint>
                          {row?.persistent_keepalive_s != null
                            ? `keepalive ${row.persistent_keepalive_s} s`
                            : "keepalive unknown"}
                        </Hint>
                      </Box>
                    </TableCell>
                    <TableCell sx={CELL_SX}>
                      {(t.subnets || []).length ? (
                        (t.subnets || []).map((s) => (
                          <Box key={s}>
                            <Mono>{s}</Mono>
                          </Box>
                        ))
                      ) : (
                        <Hint>none</Hint>
                      )}
                      <Box>
                        <Hint>
                          {t.subnets_are_virtually_assigned
                            ? "virtually assigned"
                            : "customer subnet"}
                        </Hint>
                      </Box>
                    </TableCell>
                    <TableCell
                      sx={{
                        ...CELL_SX,
                        fontWeight: 700,
                        color: isStale
                          ? TS_COLORS.red
                          : health === "healthy"
                          ? TS_COLORS.green
                          : TS_COLORS.ink3,
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {health === "never"
                        ? "never"
                        : formatRelative(t.last_handshake)}
                    </TableCell>
                    <TableCell sx={CELL_SX}>
                      {/* A row with no counters yet is "no sample", not "no
                          traffic" — an empty bar would read as the latter. */}
                      {row &&
                      (row.rx_bytes_10m != null || row.tx_bytes_10m != null) ? (
                        <>
                          <Box
                            component="span"
                            sx={{
                              display: "inline-block",
                              width: 90,
                              height: 6,
                              borderRadius: 3,
                              backgroundColor: TS_COLORS.greyTint,
                              overflow: "hidden",
                              verticalAlign: "middle",
                              mr: 1,
                            }}
                          >
                            <Box
                              component="i"
                              sx={{
                                display: "block",
                                height: "100%",
                                width: `${Math.min(
                                  100,
                                  ((rx ?? 0) / 1_800_000) * 100
                                )}%`,
                                backgroundColor: TS_COLORS.wg,
                              }}
                            />
                          </Box>
                          <Box>
                            <Hint>
                              ↓ {formatBytes(rx)} ↑{" "}
                              {formatBytes(row.tx_bytes_10m)}
                              {row.initiations_without_handshake
                                ? " (initiations only)"
                                : ""}
                            </Hint>
                          </Box>
                        </>
                      ) : (
                        <Hint>{missingTelemetry}</Hint>
                      )}
                    </TableCell>
                    <TableCell sx={CELL_SX}>
                      <StatusDot
                        tone={
                          health === "healthy" ? "ok" : isStale ? "bad" : "grey"
                        }
                        label={PEER_HEALTH_LABEL[health]}
                      />
                    </TableCell>
                    <TableCell sx={CELL_SX}>
                      <TsButton
                        variant="ghost"
                        size="sm"
                        color={TS_COLORS.wg}
                        onClick={() => {
                          setPendingDebugTunnelId(t.id);
                          setWgSubTab("debugger");
                        }}
                      >
                        Debug
                      </TsButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </ResponsiveTable>
        </Box>
      )}
      {telemetry && telemetry.status !== "ok" && tunnels.length > 0 && (
        <GapNotice
          result={telemetry}
          what="Endpoint, keepalive and transfer counters"
        />
      )}
      <Box sx={{ px: 2.5, py: 1.5, borderTop: `1px solid ${TS_COLORS.line}` }}>
        <Hint>
          Healthy means a completed handshake in the last 15 minutes.
          &ldquo;Configured&rdquo; alone is not health: a peer can be present on
          the gateway and pass no traffic.
        </Hint>
      </Box>
    </Card>
  );
};

/**
 * Applies a preselected tunnel after the debugger panel has mounted. The panel
 * resets its store on mount, so setting the id before it mounts would be
 * wiped; a parent effect runs after the child's, which is exactly late enough.
 */
const DebuggerWithPreselect = () => {
  const { pendingDebugTunnelId, setPendingDebugTunnelId, tunnels } =
    useTroubleshootingStore();
  const getServerName = useServerName();
  React.useEffect(() => {
    if (pendingDebugTunnelId != null) {
      useVpnDebuggerStore.getState().setTunnelId(pendingDebugTunnelId);
      setPendingDebugTunnelId(null);
    }
  }, [pendingDebugTunnelId, setPendingDebugTunnelId]);
  return (
    <Box sx={{ minHeight: 640, p: 2 }}>
      <VpnDebuggerPanel tunnels={tunnels} getServerName={getServerName} />
    </Box>
  );
};

export const WireGuardTab = ({ onCopy }: { onCopy: () => void }) => {
  const {
    wgSubTab,
    setWgSubTab,
    tunnels: allTunnels,
  } = useTroubleshootingStore();
  const scopedTunnels = useScopedTunnels();
  // The Debugger picks its own tunnel from every one the user can see. The
  // scope filters stay on screen, but they must not narrow this list. Peer
  // health follows the scope.
  const debugger_ = wgSubTab === "debugger";
  const tunnels = debugger_ ? allTunnels : scopedTunnels;
  const getServerName = useServerName();
  const servers = Array.from(
    new Set(tunnels.map((t) => getServerName(t.server)))
  );
  return (
    <Box
      sx={{
        border: `1px solid ${TS_COLORS.line}`,
        borderTop: `3px solid ${TS_COLORS.wg}`,
        borderRadius: "12px",
        backgroundColor: TS_SUBSECTION_BG,
        boxShadow: "0px 1px 3px rgba(0, 25, 67, 0.1)",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          gap: 0.5,
          px: 2.5,
          borderBottom: `1px solid ${TS_COLORS.line}`,
          backgroundColor: TS_SUBSECTION_BG,
          alignItems: "center",
        }}
        role="tablist"
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            pr: 1.75,
            ...MULISH,
            fontWeight: 800,
          }}
        >
          <Pill tone="wg">WG</Pill>WireGuard
        </Box>
        <SubTab
          on={wgSubTab === "peer-health"}
          onClick={() => setWgSubTab("peer-health")}
        >
          Peer health
        </SubTab>
        <SubTab
          on={wgSubTab === "debugger"}
          onClick={() => setWgSubTab("debugger")}
        >
          Debugger
        </SubTab>
        <Box sx={{ flex: 1 }} />
        <Hint>
          {servers.length === 1
            ? `Gateway ${servers[0]}, `
            : servers.length > 1
            ? `${servers.length} gateways, `
            : ""}
          {tunnels.length} peer{tunnels.length === 1 ? "" : "s"}
          {debugger_ ? "" : " in scope"}
        </Hint>
      </Box>
      {wgSubTab === "peer-health" ? (
        <PeerHealthTable onCopy={onCopy} />
      ) : (
        <DebuggerWithPreselect />
      )}
    </Box>
  );
};
